import React, { useEffect } from 'react';
import { cn } from '@/lib/utils';
import { XIcon } from './icons';

interface DrawerProps {
  /** Controls visibility. */
  open: boolean;
  /** Called when the user clicks the backdrop, presses Escape, or clicks the close button. */
  onClose: () => void;
  /** Drawer title rendered as an h2 in the header. */
  title: string;
  /** Drawer body. Scrolls when content exceeds viewport. */
  children: React.ReactNode;
  /** Additional CSS classes on the dialog panel. */
  className?: string;
}

/**
 * Side-panel drawer that slides in from the right on `sm+` breakpoints and
 * renders full-screen on mobile. Uses the native `<dialog>` element for
 * accessibility (role="dialog" implicit, Escape handled via onCancel).
 *
 * @example
 * <Drawer open={isOpen} onClose={() => setIsOpen(false)} title="Edit news">
 *   <form>...</form>
 * </Drawer>
 */
export function Drawer({ open, onClose, title, children, className }: Readonly<DrawerProps>) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    globalThis.addEventListener('keydown', onKey);
    return () => {
      globalThis.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleCancel = (e: React.SyntheticEvent<HTMLDialogElement>) => {
    e.preventDefault();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-background/60 backdrop-blur-sm">
      <button
        type="button"
        aria-label="Close drawer"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default"
        tabIndex={-1}
      />
      <dialog
        open
        aria-label={title}
        onCancel={handleCancel}
        className={cn(
          'relative m-0 flex h-full w-full max-w-none flex-col overflow-hidden border-l bg-card p-0 text-foreground shadow-xl',
          'sm:max-w-md lg:max-w-lg',
          className,
        )}
      >
        <header className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 transition-colors hover:bg-accent"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </dialog>
    </div>
  );
}

interface DrawerFooterProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Standard drawer footer with right-aligned action buttons. Place inside the
 * drawer body just below the form.
 */
export function DrawerFooter({ children, className }: Readonly<DrawerFooterProps>) {
  return (
    <div className={cn('flex justify-end gap-3 border-t border-border bg-card pt-4', className)}>
      {children}
    </div>
  );
}
